/**
 * Timezone-aware date/time primitives.
 *
 * Two kinds of value exist in this codebase and they must never be confused:
 *
 * - An **instant** is an absolute point in time. It is always an ISO string
 *   carrying an offset (`2026-07-23T07:00:00.000Z`) or a `Date`. Everything
 *   persisted in `timestamptz` columns is an instant.
 * - A **wall clock** is what a human reads off a clock on a wall: a
 *   `yyyy-MM-dd` date plus an `HH:mm` time. It is meaningless without an
 *   explicit IANA timezone.
 *
 * Converting between the two happens *only* here, and always with the
 * timezone passed in explicitly. Nothing in this module reads the host
 * timezone, so its behaviour is identical wherever the code runs.
 */

import { addDays, parseISO } from "date-fns";
import type { Locale } from "date-fns";
import { fromZonedTime, toZonedTime, formatInTimeZone } from "date-fns-tz";


/** Used when a practice timezone is not (yet) known. */
export const FALLBACK_TIMEZONE = "Europe/Zurich";

const DAY_KEY_FORMAT = "yyyy-MM-dd";
const TIME_FORMAT = "HH:mm";
const DAY_MS = 24 * 60 * 60 * 1000;

/** A `yyyy-MM-dd` calendar date, meaningful only alongside a timezone. */
export type DayKey = string;

/** A wall-clock date/time pair in some timezone. */
export interface WallClock {
  /** `yyyy-MM-dd` */
  date: DayKey;
  /** `HH:mm` */
  time: string;
}

const toDate = (value: string | Date): Date =>
  typeof value === "string" ? parseISO(value) : value;

/**
 * Converts a wall clock in `tz` to the absolute instant it denotes.
 *
 * @example
 * // Zurich is UTC+2 in July
 * wallClockToInstant("2026-07-23", "09:00", "Europe/Zurich")
 * // "2026-07-23T07:00:00.000Z"
 *
 * ## Daylight saving edge cases
 *
 * Twice a year a wall clock is not a bijection with instants, so the mapping
 * has to pick. Both policies are deliberate:
 *
 * - **Nonexistent (spring forward).** Zurich skips 02:00-03:00 on 2026-03-29,
 *   so "02:30" denotes no instant at all. It resolves **forward** to 03:30 —
 *   the clock the practice would actually be reading at that moment. Left to
 *   `fromZonedTime` alone this silently lands an hour *earlier* (01:30), which
 *   is the one direction nobody expects.
 * - **Ambiguous (fall back).** Zurich repeats 02:00-03:00 on 2026-10-25, so
 *   "02:30" denotes two instants. It resolves to the **earlier** — the
 *   pre-transition, still-summer-time occurrence. `fromZonedTime` alone returns
 *   the later one.
 *
 * Together these are the "compatible" disambiguation that `java.time` and
 * `Temporal` use, so a wall clock resolves here the way it resolves anywhere
 * else it might be interpreted.
 *
 * Neither band overlaps any practice's opening hours in Europe, so these rules
 * exist to be predictable rather than because they are load-bearing. They stop
 * being academic in zones whose transition is at midnight (`America/Santiago`,
 * `America/Havana`, `Asia/Beirut`, `Africa/Cairo`), where {@link zonedDayRange}
 * builds its lower bound from `00:00`.
 */
export function wallClockToInstant(
  date: DayKey,
  time: string,
  tz: string
): string {
  const normalized = normalizeTime(time);
  const wallClock = `${date} ${normalized}`;
  const instant = fromZonedTime(wallClock, tz);
  const rendersAsRequested = (candidate: Date) =>
    formatInTimeZone(candidate, tz, `${DAY_KEY_FORMAT} HH:mm:ss`) === wallClock;

  // A round trip only fails inside the gap: every wall clock that exists —
  // including an ambiguous one — renders back to itself. Advance by the size of
  // the transition, sampled a day later so the sample cannot land in the gap.
  if (!rendersAsRequested(instant)) {
    const gapMs =
      offsetMs(tz, new Date(instant.getTime() + DAY_MS)) - offsetMs(tz, instant);
    return new Date(instant.getTime() + gapMs).toISOString();
  }

  // Clocks going back over the surrounding day means this wall clock may occur
  // twice. Step back by the size of that shift and keep the result only if it
  // is genuinely the same wall clock — which is true exactly when it repeats.
  const shiftMs =
    offsetMs(tz, new Date(instant.getTime() - DAY_MS)) -
    offsetMs(tz, new Date(instant.getTime() + DAY_MS));
  if (shiftMs > 0) {
    const earlier = new Date(instant.getTime() - shiftMs);
    if (rendersAsRequested(earlier)) return earlier.toISOString();
  }

  return instant.toISOString();
}

/** The UTC offset `tz` is running at during `instant`, in milliseconds. */
function offsetMs(tz: string, instant: Date): number {
  const asUtc = new Date(
    formatInTimeZone(instant, tz, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
  );
  return asUtc.getTime() - instant.getTime();
}

/** Inverse of {@link wallClockToInstant}. */
export function instantToWallClock(
  instant: string | Date,
  tz: string
): WallClock {
  const d = toDate(instant);
  return {
    date: formatInTimeZone(d, tz, DAY_KEY_FORMAT),
    time: formatInTimeZone(d, tz, TIME_FORMAT),
  };
}

/** The calendar day an instant falls on, as seen from `tz`. */
export function zonedDayKey(instant: string | Date, tz: string): DayKey {
  return formatInTimeZone(toDate(instant), tz, DAY_KEY_FORMAT);
}

/**
 * Minutes elapsed since local midnight, as seen from `tz`.
 * This is the number calendar grids position rows by.
 */
export function zonedMinutesOfDay(instant: string | Date, tz: string): number {
  const zoned = toZonedTime(toDate(instant), tz);
  return zoned.getHours() * 60 + zoned.getMinutes();
}

/** The hour component (0-23) of an instant, as seen from `tz`. */
export function zonedHour(instant: string | Date, tz: string): number {
  return toZonedTime(toDate(instant), tz).getHours();
}

/** The minute component (0-59) of an instant, as seen from `tz`. */
export function zonedMinute(instant: string | Date, tz: string): number {
  return toZonedTime(toDate(instant), tz).getMinutes();
}

/** Today's calendar day in `tz`. */
export function zonedToday(tz: string): DayKey {
  return formatInTimeZone(new Date(), tz, DAY_KEY_FORMAT);
}

/**
 * Shifts a day key by `amount` days.
 *
 * Pure string/calendar arithmetic — no instant is involved, so this cannot
 * drift across a DST transition the way `addDays` on a `Date` can.
 */
export function addDaysToDayKey(dayKey: DayKey, amount: number): DayKey {
  // Anchored at UTC noon so ±12h of arithmetic slack can't roll the date over.
  const anchor = new Date(`${dayKey}T12:00:00.000Z`);
  return addDays(anchor, amount).toISOString().slice(0, 10);
}

/** ISO weekday of a day key: 1 = Monday .. 7 = Sunday. Matches Postgres `isodow`. */
export function dayKeyIsoWeekday(dayKey: DayKey): number {
  const day = new Date(`${dayKey}T12:00:00.000Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/** True when both instants fall on the same calendar day in `tz`. */
export function isSameZonedDay(
  a: string | Date,
  b: string | Date,
  tz: string
): boolean {
  return zonedDayKey(a, tz) === zonedDayKey(b, tz);
}

/** Half-open instant range `[start, end)` covering one calendar day in `tz`. */
export function zonedDayRange(
  dayKey: DayKey,
  tz: string
): { startIso: string; endIso: string } {
  return {
    startIso: wallClockToInstant(dayKey, "00:00", tz),
    endIso: wallClockToInstant(addDaysToDayKey(dayKey, 1), "00:00", tz),
  };
}

/**
 * Half-open instant range `[start, end)` covering the week containing `dayKey`.
 *
 * @param weekStartsOn 1 for Monday (the app default), 0 for Sunday.
 */
export function zonedWeekRange(
  dayKey: DayKey,
  tz: string,
  weekStartsOn: 0 | 1 = 1
): { startIso: string; endIso: string } {
  const start = zonedStartOfWeekDayKey(dayKey, weekStartsOn);
  return {
    startIso: wallClockToInstant(start, "00:00", tz),
    endIso: wallClockToInstant(addDaysToDayKey(start, 7), "00:00", tz),
  };
}

/** First day key of the week containing `dayKey`. */
export function zonedStartOfWeekDayKey(
  dayKey: DayKey,
  weekStartsOn: 0 | 1 = 1
): DayKey {
  const isoWeekday = dayKeyIsoWeekday(dayKey); // 1..7, Monday-based
  const offset = weekStartsOn === 1 ? isoWeekday - 1 : isoWeekday % 7;
  return addDaysToDayKey(dayKey, -offset);
}

/** The 7 day keys of the week containing `dayKey`, in order. */
export function zonedWeekDayKeys(
  dayKey: DayKey,
  weekStartsOn: 0 | 1 = 1
): DayKey[] {
  const start = zonedStartOfWeekDayKey(dayKey, weekStartsOn);
  return Array.from({ length: 7 }, (_, index) => addDaysToDayKey(start, index));
}

/**
 * Formats an instant using a date-fns pattern, rendered in `tz`.
 * Drop-in replacement for `format(date, pattern, { locale })`.
 */
export function formatZoned(
  instant: string | Date,
  tz: string,
  pattern: string,
  locale?: Locale
): string {
  return formatInTimeZone(toDate(instant), tz, pattern, { locale });
}

/** `HH:mm` of an instant in `tz`. */
export function formatZonedTime(instant: string | Date, tz: string): string {
  return formatInTimeZone(toDate(instant), tz, TIME_FORMAT);
}

/** `HH:mm - HH:mm` for an instant range in `tz`. */
export function formatZonedTimeRange(
  start: string | Date,
  end: string | Date,
  tz: string
): string {
  return `${formatZonedTime(start, tz)} - ${formatZonedTime(end, tz)}`;
}

/**
 * Whether `tz` is the Swiss clock.
 *
 * Times in this product are described by country, not city — nobody in
 * Switzerland says "Zurich time", they say "Swiss time". Callers that render a
 * timezone label branch on this and fall back to {@link timezoneCity}, so the
 * copy stays idiomatic today without becoming a lie the day a practice exists
 * somewhere else.
 *
 * `Europe/Zurich` only. `Europe/Busingen` (the German enclave) and
 * `Europe/Vaduz` keep the same clock but are not Swiss, so they take the city
 * label, which is true for them.
 */
export function isSwissTimezone(tz: string | null | undefined): boolean {
  return tz === "Europe/Zurich";
}

/**
 * Last segment of an IANA identifier, humanised: `Europe/Zurich` -> `Zurich`.
 * Used for the "times shown in {city} time" label.
 */
export function timezoneCity(tz: string): string {
  const segment = tz.split("/").pop() ?? tz;
  return segment.replaceAll("_", " ");
}

/** The timezone the host device is configured for. */
export function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/** Whether `tz` is an IANA zone this runtime understands. */
export function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether `tz` is a real `Region/City` zone rather than a legacy abbreviation
 * or fixed offset.
 *
 * A practice must be pinned to a *place*, because only a place carries the
 * daylight-saving rules — including their history and their future changes.
 * The legacy aliases pass {@link isValidTimezone} and pass Postgres's
 * `pg_timezone_names` check, but several of them never observe DST at all:
 * `EST` is a permanent UTC-5, so a New York practice set to `EST` would run an
 * hour off for the eight months a year that `America/New_York` is on EDT, with
 * nothing anywhere reporting an error.
 *
 * Rejects `EST`, `MST`, `HST`, `CET`, `UTC`, `GMT`, `Zulu`, `Etc/GMT-1`, ...
 * Accepts `Europe/Zurich`, `America/New_York`, `America/Argentina/Salta`, ...
 */
export function isRegionTimezone(tz: string | null | undefined): tz is string {
  if (!isValidTimezone(tz)) return false;
  if (tz.startsWith("Etc/")) return false;
  return tz.includes("/");
}

/**
 * A `Date` positioned at noon on `dayKey` in `tz`.
 *
 * Calendar cursors are days, not instants. Anchoring at midday keeps day
 * arithmetic (`addWeeks`, `subDays`) from sliding onto the neighbouring date
 * when the host offset or a DST shift moves the value by a few hours.
 */
export function dayKeyToAnchorDate(dayKey: DayKey, tz: string): Date {
  return new Date(wallClockToInstant(dayKey, "12:00", tz));
}

/** Accepts `HH:mm` or `HH:mm:ss` and returns `HH:mm:ss`. */
function normalizeTime(time: string): string {
  return /^\d{2}:\d{2}$/.test(time) ? `${time}:00` : time;
}
